// Pagina Privacy Policy - To You SRL
// Visualizza l'informativa sulla privacy (GDPR Art. 13)

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Calendar, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface LegalPage {
  page_type: string;
  title: string;
  content: string;
  version: string;
  effective_date: string;
  last_updated: string;
}

const PrivacyPolicyPage: React.FC = () => {
  const [page, setPage] = useState<LegalPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      const response = await fetch(`${API}/gdpr/legal/privacy_policy`);
      const data = await response.json();
      setPage(data);
    } catch (error) {
      console.error('Errore caricamento privacy policy:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto p-6 lg:p-8">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-48 mb-8" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto p-6 lg:p-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="mb-4 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Torna alla Home
            </Button>
          </Link>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
              {page?.title || 'Informativa sulla Privacy'}
            </h1>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              Versione {page?.version || '1.0'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Ultimo aggiornamento: {page?.last_updated ? formatDate(page.last_updated) : '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Contenuto */}
      <div className="max-w-4xl mx-auto p-6 lg:p-8">
        <Card>
          <CardContent className="p-6 lg:p-8">
            <div 
              className="prose prose-slate dark:prose-invert max-w-none
                prose-headings:font-semibold prose-headings:text-foreground
                prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-muted-foreground prose-p:leading-relaxed
                prose-li:text-muted-foreground
                prose-strong:text-foreground
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: page?.content || '' }}
            />
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="mt-8 flex flex-wrap gap-4 text-sm">
          <Link to="/terms" className="text-primary hover:underline">
            Termini e Condizioni
          </Link>
          <Link to="/cookie-policy" className="text-primary hover:underline">
            Cookie Policy
          </Link>
          <Link to="/settings" className="text-primary hover:underline">
            Gestisci le tue preferenze privacy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
